import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ColumnDef, PaginationState, SortingState } from '@tanstack/react-table';
import { Plus, RefreshCw, Download, Search } from 'lucide-react';
import { DataTable } from '@/components/ui/data-table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ActiveStatusBadge } from '@/components/ui/status-badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useLicenses } from '../services/licenseService';
import type { LicenseListItem } from '../types';
import { todayCalendarDay } from '../utils/calendarDate';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Utilization cell component
 *
 * Displays a progress bar showing license allocation percentage with color coding:
 * - Green: < 80% utilization (healthy)
 * - Yellow: 80-90% utilization (warning)
 * - Red: > 90% utilization (critical)
 */
function UtilizationCell({ allocated, total }: { allocated: number; total: number }) {
  const pct = total > 0 ? (allocated / total) * 100 : 0;
  return (
    <div className="w-full pr-2">
      <div className="flex justify-between mb-1">
        <span className="text-xs text-grey-500">{allocated}/{total}</span>
        <span className="text-xs text-grey-500 font-medium">{pct.toFixed(0)}%</span>
      </div>
      <Progress
        value={Math.min(pct, 100)}
        className="h-1.5"
        indicatorClassName={cn(
          pct >= 90 ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-success'
        )}
      />
    </div>
  );
}

/**
 * License List Page Component
 *
 * Displays a searchable, sortable, and filterable data table of all licenses
 * with server-side pagination, utilization visualization, and CSV export.
 */
const LicenseList: React.FC = () => {
  const navigate = useNavigate();
  const { canManageLicenses } = useAuth();

  // State for filters and pagination
  const [search, setSearch] = React.useState('');
  const [contractFilter, setContractFilter] = React.useState<string>('all');
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'productName', desc: false },
  ]);

  // Fetch licenses with current filters
  const { data, isLoading, error, refetch } = useLicenses({
    search: search || undefined,
    contractId: contractFilter === 'all' ? undefined : contractFilter,
    pageNumber: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
  });

  /**
   * Handle row click to navigate to license detail
   */
  const handleRowClick = (row: LicenseListItem) => {
    navigate({ to: `/licenses/${row.id}` });
  };

  /**
   * Handle create new license button
   */
  const handleCreateLicense = () => {
    navigate({ to: '/licenses/new' });
  };

  /**
   * Handle CSV export
   */
  const handleExport = () => {
    const items = data?.items;
    if (!items || items.length === 0) return;

    const headers = ['Product Name', 'Version', 'Contract', 'Type', 'Total', 'Allocated', 'Available', 'Status'];
    const rows = items.map((license: LicenseListItem) => [
      license.productName,
      license.version || '',
      license.contractName,
      license.licenseType,
      license.totalQuantity,
      license.allocatedQuantity,
      license.availableQuantity,
      license.isActive ? 'Active' : 'Inactive',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `licenses-${todayCalendarDay()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * DataTable column definitions
   */
  const columns: ColumnDef<LicenseListItem>[] = [
    {
      accessorKey: 'productName',
      header: 'Product Name',
      cell: ({ row }) => (
        <div>
          <span className="text-sm font-medium">{row.original.productName}</span>
          {row.original.version && (
            <span className="block text-xs text-grey-500">v{row.original.version}</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'contractName',
      header: 'Contract',
    },
    {
      accessorKey: 'licenseType',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.licenseType}</Badge>
      ),
    },
    {
      accessorKey: 'totalQuantity',
      header: 'Total',
      cell: ({ row }) => (
        <span className="text-center block">{row.original.totalQuantity}</span>
      ),
    },
    {
      accessorKey: 'allocatedQuantity',
      header: 'Allocated',
      cell: ({ row }) => (
        <span className="text-center block">{row.original.allocatedQuantity}</span>
      ),
    },
    {
      accessorKey: 'availableQuantity',
      header: 'Available',
      cell: ({ row }) => (
        <span
          className={cn(
            'text-center block font-medium text-sm',
            row.original.availableQuantity > 0 ? 'text-success' : 'text-destructive'
          )}
        >
          {row.original.availableQuantity}
        </span>
      ),
    },
    {
      id: 'utilization',
      header: 'Utilization',
      enableSorting: false,
      cell: ({ row }) => (
        <UtilizationCell
          allocated={row.original.allocatedQuantity}
          total={row.original.totalQuantity}
        />
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
  ];

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-semibold text-grey-800">Licenses</h1>
        {canManageLicenses && (
          <Button size="lg" onClick={handleCreateLicense} aria-label="Create new license">
            <Plus className="h-4 w-4" />
            New License
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-[2]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
            <Input
              placeholder="Search by product name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={contractFilter} onValueChange={setContractFilter}>
            <SelectTrigger className="flex-1 min-w-[200px]">
              <SelectValue placeholder="All Contracts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Contracts</SelectItem>
              {/* TODO: Populate with actual contracts from API */}
              <SelectItem value="1">Microsoft Office 365 Enterprise</SelectItem>
              <SelectItem value="2">Adobe Creative Cloud</SelectItem>
              <SelectItem value="3">Salesforce CRM</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Failed to load licenses. Please try again later.</AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <Card>
        <DataTable
          columns={columns}
          data={data?.items || []}
          rowCount={data?.totalCount || 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          onRowClick={handleRowClick}
          toolbar={
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
              <Button variant="ghost" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
          }
        />
      </Card>
    </div>
  );
};

export default LicenseList;
